import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import { IconArrowLeft, IconSend } from "@tabler/icons-react"

interface Cycle {
  id: number
  display_name: string
  user_name: string
}

interface ReviewAnswer {
  id: number
  answer_value: string
  score: number | null
  question: {
    id: number
    question_text: string
    question_type: string
    category: string
    display_category: string
  }
}

interface Review {
  id: number
  reviewer_type: string
  reviewer_name: string
  reviewer_email: string
  status: string
  score: number | null
  submitted_at: string | null
  comments: string | null
  can_be_submitted: boolean
}

interface Props {
  [key: string]: unknown
  cycle: Cycle
  review: Review
  review_answers: ReviewAnswer[]
}

function statusVariant(status: string): "default" | "secondary" | "outline" {
  switch (status) {
    case "completed":
      return "default"
    case "in_progress":
      return "secondary"
    default:
      return "outline"
  }
}

function statusLabel(status: string) {
  return status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

function formatAnswerValue(answer: ReviewAnswer) {
  const type = answer.question.question_type
  if (type === "rating_1_5" || type === "rating_1_10") {
    const max = type === "rating_1_5" ? 5 : 10
    return `${answer.answer_value} / ${max}`
  }
  if (type === "yes_no") {
    return answer.answer_value.charAt(0).toUpperCase() + answer.answer_value.slice(1)
  }
  return answer.answer_value
}

export default function PerformanceReviewShow() {
  const { cycle, review, review_answers } = usePage<Props>().props

  // Group answers by category
  const groupedAnswers = review_answers.reduce(
    (acc, answer) => {
      const cat = answer.question.display_category
      if (!acc[cat]) acc[cat] = []
      acc[cat].push(answer)
      return acc
    },
    {} as Record<string, ReviewAnswer[]>,
  )

  function handleSubmit() {
    if (confirm("Submit this review? This action cannot be undone.")) {
      router.post(`/performance_review_cycles/${cycle.id}/performance_reviews/${review.id}/submit_review`)
    }
  }

  return (
    <>
      <Head title={`${review.reviewer_type} Review - ${cycle.display_name} - Operations Center`} />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/performance_review_cycles">Performance Review Cycles</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href={`/performance_review_cycles/${cycle.id}`}>{cycle.display_name}</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                {review.reviewer_type.charAt(0).toUpperCase() + review.reviewer_type.slice(1)} Review
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
              <a href={`/performance_review_cycles/${cycle.id}`}>
                <IconArrowLeft className="h-4 w-4" />
              </a>
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold capitalize">{review.reviewer_type} Review</h1>
                <Badge variant={statusVariant(review.status)}>{statusLabel(review.status)}</Badge>
              </div>
              <p className="text-muted-foreground text-sm">
                {cycle.user_name} - {cycle.display_name}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {review.status !== "completed" && (
              <Button variant="outline" size="sm" asChild>
                <a href={`/performance_review_cycles/${cycle.id}/performance_reviews/${review.id}/edit`}>Edit</a>
              </Button>
            )}
            {review.can_be_submitted && review.status !== "completed" && (
              <Button size="sm" onClick={handleSubmit}>
                <IconSend className="mr-1 h-4 w-4" />
                Submit Review
              </Button>
            )}
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Reviewer Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Name</span>
                <span>{review.reviewer_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Email</span>
                <span>{review.reviewer_email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Type</span>
                <span className="capitalize">{review.reviewer_type}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Review Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <Badge variant={statusVariant(review.status)}>{statusLabel(review.status)}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Submitted</span>
                <span>{formatDate(review.submitted_at)}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Score</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <span className="text-3xl font-bold">{review.score ? review.score.toFixed(1) : "—"}</span>
                <p className="text-muted-foreground text-sm">/ 5.0</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {Object.entries(groupedAnswers).map(([category, answers]) => (
          <Card key={category}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">{category}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {answers.map((answer, idx) => (
                  <div key={answer.id}>
                    {idx > 0 && <Separator className="mb-4" />}
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{answer.question.question_text}</p>
                      <div className="flex items-center gap-2">
                        {(answer.question.question_type === "rating_1_5" ||
                          answer.question.question_type === "rating_1_10") && (
                          <Badge variant="outline">{formatAnswerValue(answer)}</Badge>
                        )}
                        {answer.question.question_type !== "rating_1_5" &&
                          answer.question.question_type !== "rating_1_10" && (
                            <p className="text-muted-foreground text-sm">{formatAnswerValue(answer)}</p>
                          )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}

        {review_answers.length === 0 && (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              No answers recorded yet. Edit the review to add responses.
            </CardContent>
          </Card>
        )}

        {review.comments && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Comments</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">{review.comments}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  )
}

PerformanceReviewShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
